/**
 * Matching for the replacement picker's search box.
 *
 * Extracted from the component because it is the only part of that step with a
 * decision in it, and a decision that is easy to get subtly wrong: people type
 * `gamino` for Gamiño and `rocio` for Rocío, and a picker that answers "nobody
 * matches" to a name that is plainly on the list reads as broken.
 */

/**
 * Case- and accent-insensitive form.
 *
 * `NFD` splits a letter from its diacritic and the range strips the marks, so
 * `Gamiño` and `gamino` fold to the same string. `ñ` is decomposed by NFD like
 * any other accented letter, which is what makes it work without a special case.
 */
export const foldForSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/**
 * Whether a candidate matches what was typed.
 *
 * The sub area is part of the haystack on purpose: «backend» is how somebody
 * looks for the person who can cover backend work, and they should not have to
 * know that person's name to find them.
 *
 * An empty query matches everyone — the caller shows the whole list rather than
 * an empty one.
 */
export const matchesCandidate = (
  candidate: { readonly displayName: string; readonly subAreaName: string },
  query: string,
): boolean => {
  const needle = foldForSearch(query);
  if (needle.length === 0) return true;

  return foldForSearch(`${candidate.displayName} ${candidate.subAreaName}`).includes(needle);
};
